// TL-6 Rectangle: corner→corner, Shift = square, Alt = center-out, Measurements `w,h`.
// Emits a closed 4-point polyline (SCP-4: rect is not a special type).
import { dist, type Vec } from "../core/geometry";
import { formatLength, parsePair } from "../core/units";
import { newId } from "../doc/document";
import type { PolylineEntity } from "../doc/entities";
import type { RenderView } from "../engine/renderEntities";
import { strokeWidthWorld } from "../engine/renderEntities";
import { drawReadout } from "./line";
import type { Tool, ToolCtx, ToolKeyEvent, ToolPointerEvent } from "./types";

export class RectTool implements Tool {
  readonly id = "rect" as const;
  private anchor: Vec | null = null;
  private cursor: Vec | null = null;
  private centerOut = false;

  constructor(private ctx: ToolCtx) {}

  activate(): void {
    this.reset();
  }

  deactivate(): void {
    this.reset();
  }

  cancel(): void {
    this.reset();
  }

  inProgress(): boolean {
    return this.anchor !== null;
  }

  private reset(): void {
    this.anchor = null;
    this.cursor = null;
    this.centerOut = false;
    this.ctx.setSnapBase(null);
    this.ctx.setMeasure("pair", "width,height");
    this.ctx.setHint("Rectangle: click the first corner · Alt = from center · Esc = cancel");
    this.ctx.setCursorTip(null);
    this.ctx.requestRender();
  }

  pointerDown(e: ToolPointerEvent): void {
    if (e.button !== 0) return;
    if (!this.anchor) {
      this.anchor = e.point;
      this.centerOut = e.alt;
      this.ctx.setSnapBase(e.point);
      this.ctx.setHint("Rectangle: click the opposite corner — type w,h ⏎ · Shift = square");
      this.ctx.setCursorTip("opposite corner · w,h ⏎");
      this.ctx.requestRender();
      return;
    }
    this.commitAt(e.point, e.shift);
  }

  pointerMove(e: ToolPointerEvent): void {
    this.cursor = e.point;
    this.ctx.requestRender();
  }

  /** 3.21.0: the opposite corner is not held on a line by ORTHO (Shift still makes a square). */
  ignoresOrtho(): boolean {
    return this.anchor !== null;
  }

  pointerUp(): void {}

  doubleClick(): void {}

  key(e: ToolKeyEvent): boolean {
    if (e.key === "Escape" && this.anchor) {
      this.reset();
      return true;
    }
    return false;
  }

  measurement(raw: string): boolean {
    if (!this.anchor) return false;
    const { units } = this.ctx.docUnits();
    const pair = parsePair(raw, units);
    if (!pair) return false;
    const [w, h] = pair;
    if (Math.abs(w) < 1e-9 || Math.abs(h) < 1e-9) return false; // EC-TL-6
    // direction signs from current cursor, default +,+
    const sx = this.cursor && this.cursor.x < this.anchor.x ? -1 : 1;
    const sy = this.cursor && this.cursor.y < this.anchor.y ? -1 : 1;
    this.commitAt({ x: this.anchor.x + sx * Math.abs(w), y: this.anchor.y + sy * Math.abs(h) }, false);
    return true;
  }

  private cornersFor(opposite: Vec, square: boolean): Vec[] | null {
    const a = this.anchor!;
    let dx = opposite.x - a.x;
    let dy = opposite.y - a.y;
    if (square) {
      const m = Math.max(Math.abs(dx), Math.abs(dy));
      dx = Math.sign(dx || 1) * m;
      dy = Math.sign(dy || 1) * m;
    }
    let p0: Vec;
    let p2: Vec;
    if (this.centerOut) {
      p0 = { x: a.x - dx, y: a.y - dy };
      p2 = { x: a.x + dx, y: a.y + dy };
    } else {
      p0 = a;
      p2 = { x: a.x + dx, y: a.y + dy };
    }
    if (Math.abs(p2.x - p0.x) < 1e-9 || Math.abs(p2.y - p0.y) < 1e-9) return null; // EC-TL-6 zero area
    return [p0, { x: p2.x, y: p0.y }, p2, { x: p0.x, y: p2.y }];
  }

  private commitAt(opposite: Vec, square: boolean): void {
    const corners = this.cornersFor(opposite, square);
    if (!corners) return; // stay in tool, waiting for a valid corner
    const doc = this.ctx.store.doc;
    const entity: PolylineEntity = {
      id: newId(),
      type: "polyline",
      layerId: this.ctx.activeLayerId(),
      locked: false,
      hidden: false,
      points: corners,
      closed: true,
      style: structuredClone(doc.defaultStyle)
    };
    this.ctx.store.dispatch({
      kind: "addEntities",
      entities: [entity],
      z: [{ layerId: entity.layerId, index: -1 }]
    });
    const entityId = entity.id;
    const frozen = corners.map((p) => ({ ...p }));
    this.ctx.registerReapply((rawValue) => {
      const { units } = this.ctx.docUnits();
      const pair = parsePair(rawValue, units);
      if (!pair) return false;
      const [w, h] = pair;
      if (Math.abs(w) < 1e-9 || Math.abs(h) < 1e-9) return false;
      const p0 = frozen[0]!;
      const sx = Math.sign(frozen[2]!.x - p0.x) || 1;
      const sy = Math.sign(frozen[2]!.y - p0.y) || 1;
      const p2 = { x: p0.x + sx * Math.abs(w), y: p0.y + sy * Math.abs(h) };
      const pts = [p0, { x: p2.x, y: p0.y }, p2, { x: p0.x, y: p2.y }];
      this.ctx.store.dispatch({
        kind: "updateEntities",
        patches: [{ id: entityId, before: { points: frozen }, after: { points: pts } }]
      });
      return true;
    });
    this.reset();
  }

  renderDynamic(v: RenderView): void {
    if (!this.anchor || !this.cursor) return;
    const corners = this.cornersFor(this.cursor, false);
    const doc = this.ctx.store.doc;
    const { ctx } = v;
    ctx.strokeStyle = doc.defaultStyle.strokeColor;
    ctx.lineWidth = strokeWidthWorld(doc.defaultStyle, v.cam, v.dpr);
    if (corners) {
      ctx.beginPath();
      ctx.moveTo(corners[0]!.x, corners[0]!.y);
      for (let i = 1; i < 4; i++) ctx.lineTo(corners[i]!.x, corners[i]!.y);
      ctx.closePath();
      ctx.stroke();
    }
    const { units, precision } = this.ctx.docUnits();
    const w = Math.abs(this.cursor.x - this.anchor.x) * (this.centerOut ? 2 : 1);
    const h = Math.abs(this.cursor.y - this.anchor.y) * (this.centerOut ? 2 : 1);
    if (dist(this.cursor, this.anchor) > 1e-9) {
      drawReadout(v, this.cursor, `${formatLength(w, units, precision)} × ${formatLength(h, units, precision)}`);
    }
  }
}
